import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import Box from '@tds/core-box';
import Button from '@tds/core-button';
import Spinner from '@tds/core-spinner';

import request from '../../../../utils/request';
import { useLocalizedStringsContext } from '../../../../context/LocalizedStringsContext';
import { ErrorMessage, FieldLabelWrapper } from '../../css';
import FieldGuide from '../../common/fieldGuide';
import FieldTitle from '../../common/FieldTitle';
import { uploadCustomerReadinessChecklist } from '../../../../utils/constant';

const FileUpload = ({
  name,
  label,
  required,
  addFileToList,
  allowedFileTypes,
  maximumFileSize,
  orderId,
  tooltip
}) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    general: {
      fileSelectError,
      fileUploadingError,
      fileSizeError,
      uploading,
      uploadLabel
    }
  } = localizedStrings;
  const [selectedFile, setSelectedFile] = useState();
  const [showError, setShowError] = useState({ isVisible: false, message: '' });
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef();

  const validateFileSize = file => {
    const fileSize = file.size / 1024 / 1024;
    if (fileSize > maximumFileSize) {
      setShowError({
        isVisible: true,
        message: fileSizeError.replace('**SIZE**', maximumFileSize)
      });
      inputRef.current.value = '';
      return false;
    }
    return true;
  };

  const renameFile = (originalFile, newName) => {
    return new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified
    });
  };

  const onFileSelect = e => {
    let file = null;
    if (name === uploadCustomerReadinessChecklist) {
      const fileExtension = e.target.files[0].type.replace(/(.*)\//g, '');
      file = renameFile(
        e.target.files[0],
        `Customer readiness checklist.${fileExtension}`
      );
    } else {
      // eslint-disable-next-line prefer-destructuring
      file = e.target.files[0];
    }

    if (!validateFileSize(file)) return;

    setShowError({ isVisible: false, message: '' });
    setSelectedFile(file);
  };

  const uploadFile = () => {
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('typeId', name);
    return request.post(`/orders/${orderId}/upload-file`, formData);
  };

  const onFileUpload = async () => {
    if (!selectedFile) {
      setShowError({
        isVisible: true,
        message: fileSelectError
      });
      return;
    }
    setIsUploading(true);
    try {
      const result = await uploadFile();
      if (result) {
        const {
          data: { orderFile }
        } = result;
        addFileToList(orderFile);
        setIsUploading(false);
        setSelectedFile(null);
        inputRef.current.value = '';
      }
    } catch (error) {
      setShowError({
        isVisible: true,
        message: fileUploadingError
      });
      setIsUploading(false);
      throw error;
    }
  };

  return (
    <Box between={2}>
      <FieldLabelWrapper>
        <FieldTitle required={required} label={label} />
        {tooltip && <FieldGuide tooltip={tooltip} />}
      </FieldLabelWrapper>
      <Box>
        <input
          type="file"
          name={name}
          onChange={onFileSelect}
          accept={allowedFileTypes}
          ref={inputRef}
        />
      </Box>
      <Box>
        <Spinner label={uploading} size="small" spinning={isUploading} inline>
          <Button onClick={onFileUpload}>{uploadLabel}</Button>
        </Spinner>
        {showError.isVisible && (
          <ErrorMessage>{showError.message}</ErrorMessage>
        )}
      </Box>
    </Box>
  );
};

FileUpload.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  addFileToList: PropTypes.func.isRequired,
  allowedFileTypes: PropTypes.string.isRequired,
  maximumFileSize: PropTypes.number.isRequired,
  orderId: PropTypes.number.isRequired,
  tooltip: PropTypes.string
};

FileUpload.defaultProps = {
  tooltip: ''
};

export default FileUpload;
